import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { useContext, useState } from 'react';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import {
  type TableTextDefaultTagSelectors,
  TextDefaultStyleTypesIdentifier,
  type TextDefaultTagSelectors,
  selectAllTextDefaultStyles,
} from 'components/editor/grid/reduxStore/textDefaultStylingSlice';
import { combineDefaultStyles, tableStyleToRowClassMap } from '../combineDefaultStyles';
import { DocumentSettingsContext } from '../../DocumentSettings/DocumentDesignSettings/DocumentSettingsContext';
import { DefaultStyleProperties } from './DefaultStyleProperties';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import { selectTableRowStyles, TableRowDefaultSelectorsEnum } from 'components/editor/grid/reduxStore/tableRowDefaultStyleSlice';

export const ROW_BG_COLOR_ATTRIBUTE_NAME = '--table-row-default-background-color';
export const ROW_ALT_BG_COLOR_ATTRIBUTE_NAME = '--table-row-alternate-background-color';

export function FormatSettings() {
  const { t } = useTranslation();
  const defaultStyles = combineDefaultStyles(useAppSelector(selectAllTextDefaultStyles), useAppSelector(selectTableRowStyles));
  const { updateTextDefaultStyle, updateTableRowStyle } = useContext(DocumentSettingsContext);
  const activeTextStyle = useAppSelector((state) => state.blockStyleSettings.activeTextStyle);
  const [selectedKey, setSelectedKey] = useState(activeTextStyle ?? defaultStyles[0]?.title ?? '');
  const activeDefaultStyle = defaultStyles.find((s) => s.title === selectedKey);
  const label = t('document.grid.default_styles.style');
  const labelId = `${label}-id`;

  const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;
    setSelectedKey(value);
  };

  const handleTextStyleChange = (attributeName: string, value: string) => {
    if (!selectedKey) return;

    updateTextDefaultStyle({
      type: selectedKey as TextDefaultTagSelectors | TableTextDefaultTagSelectors,
      style: { [attributeName]: value },
      TextDefaultStyleTypes: activeDefaultStyle?.rowStyles
        ? TextDefaultStyleTypesIdentifier.TABLE
        : TextDefaultStyleTypesIdentifier.DOCUMENT,
    });
  };

  const handleRowStyleChange = (attributeName: string, value: string) => {
    const rowSelector = tableStyleToRowClassMap[selectedKey];
    if (!rowSelector) return;

    updateTableRowStyle({
      type: rowSelector,
      style: { [attributeName]: value },
    });
  };

  return (
    <Stack spacing={2} pb={6} data-testid="format-settings">
      <Stack spacing={1}>
        <FormControl size="small" fullWidth>
          <InputLabel id={labelId} shrink>
            {label}
          </InputLabel>
          <Select
            value={selectedKey}
            onChange={handleSelectChange}
            input={<OutlinedInput notched={true} label={label} />}
            MenuProps={{ disableScrollLock: true }}
            data-testid="format-default-style-select"
            label={label}
            labelId={labelId}
            sx={{ '& .MuiSelect-select': { padding: '8px 12px' } }}
          >
            {defaultStyles.map((style) => (
              <MenuItem key={style.title} value={style.title}>
                {t(`document.grid.default_styles.${style.title?.replace('.', '')}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption">{t('document.grid.default_styles.style_description')}</Typography>
      </Stack>
      {activeDefaultStyle && (
        <DefaultStyleProperties
          textStyle={activeDefaultStyle.textStyles}
          onTextStyleChange={handleTextStyleChange}
          rowStyle={activeDefaultStyle.rowStyles}
          onRowStyleChange={handleRowStyleChange}
          showAlternateRowColor={tableStyleToRowClassMap[selectedKey] === TableRowDefaultSelectorsEnum.BODY}
        />
      )}
    </Stack>
  );
}
